<template>
  <div class="orders-list">
    <!-- Agregar este alert al principio de la lista de órdenes -->
    <div class="alert alert-info mb-4 fs-6" v-if="sortedOrders.length > 0">
      <i class="fas fa-envelope me-2"></i>
        Todas las actualizaciones sobre tus pedidos serán enviadas a tu email registrado. 
        Por favor, revisa tu bandeja de entrada y spam.
    </div>

    <!-- Loading State -->
    <div v-if="props.isLoading || localLoading" class="orders-skeleton">
      <div class="skeleton-order mb-3">
        <div class="skeleton-header"></div>
        <div class="skeleton-content" v-for="i in 3" :key="i">
          <div class="skeleton-thumbnail"></div>
          <div class="skeleton-details">
            <div class="skeleton-text w-50"></div>
            <div class="skeleton-text w-75"></div>
            <div class="skeleton-text w-25"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Orders List -->
    <div v-else-if="sortedOrders.length > 0" class="orders-list">
      <div v-for="order in sortedOrders" :key="order.id" class="order-card mb-4">
        <template v-if="order.order_products && order.order_products.length > 0">
          <div class="order-header">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="order-id text-white">#{{ order.id }}</span>
              <div class="d-flex align-items-center gap-2">
                <span 
                  class="badge"
                  :class="getStatusBadgeClass(order.status)"
                >
                  <i :class="getStatusIcon(order.status)" class="me-2"></i>
                  {{ getStatusLabel(order.status) }}
                </span>
              </div>
            </div>
            <small class="text-muted">{{ formatDate(order.created_at) }}</small>
          </div>

          <div class="order-content">
            <!-- Products List -->
            <div class="products-summary mb-3">
              <div v-for="product in order.order_products" :key="product.product" class="product-item">
                <template v-if="product.product_name">
                  <img 
                    :src="product.product_image" 
                    :alt="product.product_name" 
                    class="product-thumbnail"
                  >
                  <div class="flex-grow-1">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <p class="mb-0 fw-bold">{{ product.product_name }}</p>
                        <small class="text-muted d-block">{{ product.product_brand || 'Sin marca' }}</small>
                      </div>
                      <!-- Botón de eliminar producto si la orden está pendiente -->
                      <button 
                        v-if="order.status === 'pending'"
                        class="btn btn-sm btn-link text-danger"
                        @click="confirmRemoveProduct(order, product)"
                        title="Eliminar producto"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mt-1">
                      <span class="text-muted">Cantidad: {{ product.quantity }}</span>
                      <span class="text-success">{{ formatPrice(product.price_at_time) }}</span>
                    </div>
                  </div>
                </template>
            </div>
            <div class="d-flex justify-content-end mt-3">
                <button 
                v-if="order.status === 'pending'"
                class="btn btn-lg btn-danger"
                @click="confirmCancelOrder(order)"
                title="Cancelar pedido"
              >
                <i class="fas fa-times me-2"></i>Cancelar pedido
              </button>
            </div>
            </div>

            <!-- Order Details -->
            <div class="order-details mt-3">
              <!-- ...existing order details... -->
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-5">
      <i class="fas fa-shopping-bag fa-3x text-muted mb-3"></i>
      <h4 class="text-muted">No tienes pedidos activos</h4>
      <router-link to="/productos" class="btn btn-primary mt-3">
        <i class="fas fa-store me-2"></i>
        Ir a la Tienda
      </router-link>
    </div>

    <!-- Confirmation Modal -->
    <div class="modal fade mt-5 pt-5" id="confirmationModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content bg-dark">
          <div class="modal-header border-bottom border-secondary">
            <h5 class="modal-title">{{ modalTitle }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body text-white">
            {{ modalMessage }}
          </div>
          <div class="modal-footer border-top border-secondary">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="handleConfirmAction"
              :disabled="isProcessing"
            >
              <span v-if="isProcessing">
                <i class="fas fa-spinner fa-spin me-2"></i>Procesando...
              </span>
              <span v-else>Confirmar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { updateOrder, getUserOrders } from '@/services/orders';
import { defineProps } from 'vue';

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false
  }
});

const toast = useToast();
const bootstrap = window.bootstrap;
const userOrders = ref([]);
const localLoading = ref(true);

// Estados locales
const modalTitle = ref('');
const modalMessage = ref('');
const selectedOrder = ref(null);
const selectedProduct = ref(null);
const currentAction = ref('');
const isProcessing = ref(false);

// Watch para actualizar userOrders cuando cambien las props
watch(() => props.orders, (newOrders) => {
  if (newOrders) {
    userOrders.value = newOrders;
    localStorage.setItem('userOrders', JSON.stringify({ orders: newOrders }));
  }
}, { immediate: true }); // immediate: true hace que se ejecute inmediatamente

onMounted(async () => {
  try {
    localLoading.value = true;
    // Limpiar caché y obtener órdenes frescas al montar
    localStorage.removeItem('userOrders');
    localStorage.removeItem('ordersTimestamp');
    await refreshOrders();
  } catch (error) {
    console.error('Error in mounted:', error);
    toast.error('Error al cargar los pedidos');
  } finally {
    localLoading.value = false;
  }
});

// Modificar el computed de sortedOrders para manejar mejor el caso de array vacío
const sortedOrders = computed(() => {
  if (!userOrders.value || userOrders.value.length === 0) return [];
  
  return [...userOrders.value]
    .filter(order => order.status !== 'cancelled')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

// Helpers
const getStatusBadgeClass = (status) => ({
  'bg-warning': status === 'pending',
  'bg-success': status === 'completed',
  'bg-danger': status === 'cancelled'
});

const getStatusIcon = (status) => ({
  pending: 'fas fa-clock',
  completed: 'fas fa-check',
  cancelled: 'fas fa-times'
}[status]);

const getStatusLabel = (status) => ({
  pending: 'Pendiente',
  completed: 'Completado',
  cancelled: 'Cancelado'
}[status]);

const formatDate = (date) => {
  return new Date(date).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(price);
};

const showModal = (title, message) => {
  modalTitle.value = title;
  modalMessage.value = message;
  const modal = new bootstrap.Modal(document.getElementById('confirmationModal'));
  modal.show();
};

const confirmCancelOrder = (order) => {
  selectedOrder.value = order;
  currentAction.value = 'cancelOrder';
  showModal(
    'Cancelar Pedido',
    `¿Estás seguro que deseas cancelar el pedido #${order.id}?`
  );
};

const confirmRemoveProduct = (order, product) => {
  selectedOrder.value = order;
  selectedProduct.value = product;
  currentAction.value = 'removeProduct';
  showModal(
    'Eliminar Producto',
    `¿Estás seguro que deseas eliminar ${product.product_name} del pedido #${order.id}?`
  );
};

const refreshOrders = async () => {
  try {
    localLoading.value = true;
    const orders = await getUserOrders();
    console.log('Orders loaded in UserOrdersList:', orders); // Para debug
    userOrders.value = orders || [];
  } catch (error) {
    console.error('Error refreshing orders:', error);
    toast.error('Error al actualizar los pedidos');
    userOrders.value = [];
  } finally {
    localLoading.value = false;
  }
};

const handleConfirmAction = async () => {
  isProcessing.value = true;
  try {
    if (currentAction.value === 'cancelOrder') {
      // Format the products data correctly
      const products = selectedOrder.value.order_products.map(product => ({
        product_id: product.product,
        blister_id: product.blister,  // Changed from product.blister.id to product.blister
        quantity: product.quantity,
        price_at_time: product.price_at_time
      }));

      await updateOrder(selectedOrder.value.id, {
        status: 'cancelled',
        products: products
      });

      toast.success('Pedido cancelado correctamente');
    } else if (currentAction.value === 'removeProduct') {
      // For removing a product, only send the updated products list
      const updatedProducts = selectedOrder.value.order_products
        .filter(p => p.product !== selectedProduct.value.product)
        .map(product => ({
          product_id: product.product,
          blister_id: product.blister,
          quantity: product.quantity
        }));
      
      if (updatedProducts.length === 0) {
        await updateOrder(selectedOrder.value.id, { status: 'cancelled' });
        toast.success('Pedido cancelado por falta de productos');
      } else {
        await updateOrder(selectedOrder.value.id, { products: updatedProducts });
        toast.success('Producto eliminado del pedido');
      }
    }
    
    localStorage.removeItem('userOrders');
    localStorage.removeItem('orders');
    localStorage.removeItem('products');
    await refreshOrders();
    
    const modal = bootstrap.Modal.getInstance(document.getElementById('confirmationModal'));
    modal.hide();
  } catch (error) {
    console.error('Error:', error);
    toast.error('Error al procesar la acción');
  } finally {
    isProcessing.value = false;
  }
};
</script>

<style scoped>
.orders-skeleton {
  min-height: 200px;
}

.skeleton-order {
  height: 350px;
  background: #1a1a1a;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skeleton-header {
  height: 50px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-content {
  display: flex;
  gap: 1rem;
}

.skeleton-thumbnail {
  width: 60px;
  height: 60px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

.skeleton-details {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skeleton-text {
  height: 15px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.order-card {
  background: var(--black-color-2);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease;
}

.order-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.order-header {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.order-content {
  padding: 1rem;
}

.product-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.product-item:hover {
  transform: translateX(5px);
  background: rgba(255, 255, 255, 0.08);
}

.product-thumbnail {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.25rem;
}

.badge {
  padding: 0.5rem 0.75rem;
  font-weight: 500;
  font-size: 0.85rem;
}

.btn-link {
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  opacity: 0.7;
  transition: all 0.2s ease;
}

.btn-link:hover {
  opacity: 1;
  background: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
}

.btn-outline-danger {
  background: transparent;
  border: 1px solid #dc3545;
  color: #dc3545;
  padding: 0.25rem 0.5rem;
  transition: all 0.2s ease;
}

.btn-outline-danger:hover {
  background: #dc3545;
  color: white;
}

.modal-content {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 768px) {
  .order-header {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .product-item {
    flex-direction: column;
  }
  
  .product-thumbnail {
    width: 100%;
    height: 120px;
  }
}
</style>